/**
 * Normalize markdown heading levels to their OUTLINE DEPTH before rendering.
 *
 * ## The problem
 *
 * `response-prose` styles a heading by its absolute markdown level, and the
 * model does not pick those consistently. In one observed answer:
 *
 * ```markdown
 * ## 1. Opening the conversation
 * #### Set up                          <- depth 1, tagged h4
 *
 * ## 4. Decisions to clarify and document
 * ### A. Escalation for a future exacerbation   <- depth 1, tagged h3
 * ```
 *
 * "Set up" and "A. Escalation…" sit at the same place in the outline and do
 * the same job, but one rendered as a body-size label and the other as an
 * Exposure subsection. Collapsing more levels onto one tier only hides this —
 * it would break the first time an answer genuinely nests three deep.
 *
 * ## The fix
 *
 * A heading's tier is its depth in the document outline, not its tag. Walk the
 * headings in order keeping a stack of open levels; a heading's depth is how
 * many shallower headings are still open above it. Both headings above are at
 * depth 1, so both render the same, and a real third level still gets its own
 * tier.
 *
 * Emitted levels are contiguous (`h2` → `h3` → `h4`) so heading navigation and
 * screen readers see a well-formed outline — the model's own output routinely
 * skips levels, which is an accessibility defect independent of the styling.
 * `h1` is deliberately never emitted: the page owns the document's single `h1`,
 * and an answer is never that.
 *
 * ## Streaming
 *
 * Pure function of the text so far, so it is safe to recompute on every chunk.
 * A heading's depth depends only on headings BEFORE it, which never change as
 * more text arrives — a level, once rendered, cannot be revised by later
 * content.
 *
 * ## Offsets
 *
 * Rewriting `####` to `###` changes the text length, and Evidence chat slices
 * the same string by backend-supplied citation indices. So the transform also
 * returns `mapIndex`, which carries a source offset to the same logical
 * position in the output. Without it every citation after the first heading
 * would drift.
 */

/** Leading `#` run of an ATX heading. A `#` with no following space is not a heading. */
const ATX_HEADING = /^( {0,3})(#{1,6})(?=[ \t]|$)/;

/** Opening or closing fence of a code block. */
const CODE_FENCE = /^ {0,3}(`{3,}|~{3,})/;

/**
 * Outline depth to emitted level.
 *
 * `response-prose` renders two visual tiers — section (h1, h2) and label
 * (h3-h6) — so depth 0 is a section and everything below it is a label, and
 * there is nothing to gain from emitting `h5` or `h6`.
 *
 * Depth 1 and depth 2 still emit DIFFERENT levels despite looking identical.
 * Collapsing them here would skip a level in the markup, which heading
 * navigation and screen readers do read; that two tiers share a look is a
 * presentation decision and does not belong in the outline.
 */
const DEPTH_TO_LEVEL = [2, 3, 4] as const;

export interface NormalizedHeadings {
  /** The markdown with heading levels rewritten to outline depth. */
  text: string;
  /** Carry an index in the source text to the same position in `text`. */
  mapIndex: (sourceIndex: number) => number;
}

/**
 * Rewrite heading levels to outline depth, and return an index map alongside.
 *
 * Setext headings (`Title` underlined with `===` / `---`) are left alone: the
 * models emit ATX exclusively, and rewriting a setext heading would mean
 * replacing two lines with one and complicating the index map for a case that
 * does not occur.
 */
export const normalizeHeadingDepthWithMap = (markdown: string): NormalizedHeadings => {
  const lines = markdown.split('\n');

  const outLines: string[] = [];
  /** Source offset of each line's first character. */
  const lineStart: number[] = [];
  /** Cumulative length change contributed by all EARLIER lines. */
  const deltaBefore: number[] = [];
  /** Length change contributed by this line itself. */
  const lineDelta: number[] = [];
  /** Source column at which this line's heading marker ends; 0 when not a heading. */
  const markerEnd: number[] = [];

  /** Levels of the headings still open above the cursor. */
  const openLevels: number[] = [];
  let fence: string | null = null;
  let offset = 0;
  let delta = 0;

  for (const line of lines) {
    lineStart.push(offset);
    deltaBefore.push(delta);

    const fenceMatch = CODE_FENCE.exec(line);
    const heading = fence === null && !fenceMatch ? ATX_HEADING.exec(line) : null;

    if (fenceMatch) {
      // A fence closes only on its own marker character, so ``` inside a ~~~
      // block stays literal.
      const marker = fenceMatch[1]![0]!;
      if (fence === null) fence = marker;
      else if (fence === marker) fence = null;
    }

    if (!heading) {
      outLines.push(line);
      lineDelta.push(0);
      markerEnd.push(0);
      offset += line.length + 1;
      continue;
    }

    const indent = heading[1]!;
    const rawLevel = heading[2]!.length;

    // Close every heading at or above this level — those sections have ended.
    while (openLevels.length > 0 && openLevels[openLevels.length - 1]! >= rawLevel) {
      openLevels.pop();
    }
    const depth = openLevels.length;
    openLevels.push(rawLevel);

    const level = DEPTH_TO_LEVEL[Math.min(depth, DEPTH_TO_LEVEL.length - 1)]!;
    const rewritten = indent + '#'.repeat(level) + line.slice(indent.length + rawLevel);

    outLines.push(rewritten);
    lineDelta.push(rewritten.length - line.length);
    markerEnd.push(indent.length + rawLevel);
    delta += rewritten.length - line.length;
    offset += line.length + 1;
  }

  const text = outLines.join('\n');

  const mapIndex = (sourceIndex: number): number => {
    if (sourceIndex <= 0) return 0;

    // Last line that starts at or before the index.
    let lo = 0;
    let hi = lineStart.length - 1;
    let line = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (lineStart[mid]! <= sourceIndex) {
        line = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    const column = sourceIndex - lineStart[line]!;
    const marker = markerEnd[line]!;
    const shift = lineDelta[line]!;
    // Past the marker the whole remainder of the line has moved by `shift`;
    // inside it there is no meaningful target, so clamp to the marker's end.
    const newColumn =
      column >= marker ? column + shift : Math.min(column, marker + shift);

    const mapped = lineStart[line]! + deltaBefore[line]! + newColumn;
    return Math.max(0, Math.min(mapped, text.length));
  };

  return { text, mapIndex };
};

/** `normalizeHeadingDepthWithMap` for a caller that holds no offsets into the text. */
export const normalizeHeadingDepth = (markdown: string): string =>
  normalizeHeadingDepthWithMap(markdown).text;
